<template>
  <section class="topic-highlights" aria-labelledby="home-topics-title">
    <header class="highlights-header">
      <div class="title-group">
        <span class="eyebrow">题材雷达</span>
        <h2 id="home-topics-title">{{ isHistorical ? '历史强势题材' : '今日强势题材' }}</h2>
        <span class="top-badge">TOP 3</span>
      </div>
      <div class="header-actions">
        <div class="history-navigator" aria-label="切换强势题材交易日">
          <b-button size="sm" variant="outline-secondary" :disabled="loading || !canGoOlder" title="前一交易日" aria-label="前一交易日" @click="goOlder">
            <b-icon icon="chevron-left"></b-icon>
          </b-button>
          <b-form-datepicker
            id="home-topic-history-datepicker"
            v-model="pickerDate"
            size="sm"
            locale="zh"
            :max="latestNavigableDate || undefined"
            :date-disabled-fn="isHistoryDateDisabled"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            button-only
            right
            label-calendar="选择交易日"
            :disabled="loading"
            @input="onCalendarChange"
          ></b-form-datepicker>
          <span class="history-date">{{ navigationDateLabel }}</span>
          <b-button size="sm" variant="outline-secondary" :disabled="loading || !canGoNewer" title="后一交易日" aria-label="后一交易日" @click="goNewer">
            <b-icon icon="chevron-right"></b-icon>
          </b-button>
        </div>
        <span class="snapshot-label"><i></i>{{ snapshotLabel }}</span>
        <router-link class="library-link" to="/wonder/topic/table">全部题材 <span>→</span></router-link>
      </div>
    </header>

    <div class="snapshot-meta" aria-live="polite">
      <span v-if="data">{{ displayDate }}{{ isHistorical ? '收盘' : '行情' }} · {{ isHistorical ? '历史入库结果' : calculatedTime + '更新' }}</span>
      <span v-else>综合上涨强度、覆盖度与领涨表现</span>
      <button type="button" :disabled="loading" title="读取最新缓存" @click="fetchTopics">{{ loading ? '读取中…' : '刷新' }}</button>
    </div>
    <p v-if="error" class="ranking-notice" role="status">{{ error }}</p>
    <div v-if="loading && !data" class="ranking-empty">正在筛选题材，请稍候…</div>
    <div v-else-if="data && !data.items.length" class="ranking-empty">{{ emptyMessage }}</div>

    <div v-if="data && data.items.length" :class="['topic-grid', 'topic-count-' + Math.min(data.items.length, 3)]">
      <article v-for="(topic, index) in data.items" :key="topic.unique_key" class="topic-highlight" :class="{ 'topic-first': index === 0 }">
        <div class="topic-topline">
          <div class="topic-heading">
            <span class="topic-rank"><b>0{{ index + 1 }}</b></span>
            <h3 :title="topic.name"><router-link :to="topicLink(topic)" @click.native="track(topic)">{{ displayTopicName(topic.name) }}</router-link></h3>
          </div>
          <span class="strength-score">强度 <b>{{ formatNumber(topic.score, 1) }}</b></span>
        </div>
        <div class="topic-mainline">
          <strong :class="['topic-return', changeClass(topic.today_pct)]">{{ signedNumber(topic.today_pct, 2) }}<small>%</small></strong>
          <div class="topic-metrics">
            <span>上涨 <b>{{ topic.up_ratio }}%</b><small>（{{ topic.up_count }}/{{ topic.stock_count }}）</small></span>
            <span>领涨 <b>{{ topic.leader_count }}</b></span>
            <span>涨停 <b>{{ topic.limit_up_count }}</b></span>
            <span>跌停 <b>{{ topic.limit_down_count || 0 }}</b></span>
          </div>
        </div>
        <div class="breadth-track" :aria-label="'上涨占比 ' + topic.up_ratio + '%'" role="img"><span :style="{ width: topic.up_ratio + '%' }"></span></div>
      </article>
    </div>
    <details class="ranking-method">
      <summary>如何选出这三个题材？</summary>
      <p>综合分 = 平均涨幅（40 分，5% 封顶）+ 上涨占比（20 分）+ 涨幅超过 5% 的占比（20 分，30% 封顶）+ 涨停占比（20 分，10% 封顶）。至少 5 家有效行情、覆盖至少 80% 成分股、平均涨幅为正且至少半数上涨才入选。</p>
      <p>排名反映当前强度，不是收益预测或买入信号。</p>
    </details>
  </section>
</template>

<script>
import { request_zzquant } from '@/network/request'

export default {
  name: 'HomeTopicHighlights',
  data() {
    return { data: null, loading: false, error: '', timer: null, disposed: false, historyDates: [], selectedDate: '', pickerDate: '', liveTradeDate: '', requestVersion: 0 }
  },
  computed: {
    isHistorical() {
      return Boolean(this.selectedDate)
    },
    latestNavigableDate() {
      return this.liveTradeDate || this.historyDates[0] || ''
    },
    navigationDate() {
      return this.selectedDate || this.liveTradeDate || (this.data && this.data.trade_date) || this.historyDates[0] || ''
    },
    navigationDateLabel() {
      if (!this.navigationDate) return '选择日期'
      return this.navigationDate.slice(5).replace('-', '/') + (this.isHistorical ? '' : ' 实时')
    },
    navigationDateIndex() {
      return this.historyDates.indexOf(this.navigationDate)
    },
    canGoOlder() {
      return this.navigationDateIndex >= 0 && this.navigationDateIndex < this.historyDates.length - 1
    },
    canGoNewer() {
      return Boolean(this.selectedDate) && this.navigationDateIndex >= 0
    },
    snapshotLabel() {
      if (!this.data) return '等待行情'
      if (this.error) return '上次成功获取的排名'
      if (this.isHistorical) return '历史收盘强度排名'
      if (this.data.cache_status === 'warming_up') return '等待竞价后的首次题材更新'
      if (this.data.source === 'auction') return '竞价行情综合排名'
      return this.data.is_trading ? '盘中综合排名 · 跟随市场情绪每 5 分钟更新' : '今日收盘排名'
    },
    calculatedTime() {
      if (!this.data || !this.data.calculated_at) return '暂无'
      const time = new Date(this.data.calculated_at)
      return Number.isNaN(time.getTime()) ? '未知' : time.toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
    },
    displayDate() {
      if (!this.data || !this.data.trade_date) return '今日'
      return this.data.trade_date.slice(5).replace('-', '/')
    },
    emptyMessage() {
      if (this.isHistorical) return '该交易日没有满足上涨覆盖与行情完整度要求的题材。'
      if (this.data && this.data.cache_status === 'warming_up') return '今日题材排名将在竞价行情更新后生成。'
      return '当前没有满足上涨覆盖与行情完整度要求的题材，暂不强行推荐。稍后再来看看。'
    }
  },
  mounted() {
    this.fetchHistoryDates()
    this.fetchTopics()
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibility)
  },
  methods: {
    topicLink(topic) { return '/wonder/topic/detail/' + encodeURIComponent(topic.unique_key) },
    displayTopicName(name) {
      return String(name || '').replace(/\s*[（(][^（）()]*[）)]/g, '').trim()
    },
    formatNumber(value, digits) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(digits) : '--'
    },
    signedNumber(value, digits) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${number > 0 ? '+' : ''}${number.toFixed(digits)}`
    },
    changeClass(value) {
      const number = Number(value)
      return number > 0 ? 'is-up' : number < 0 ? 'is-down' : 'is-flat'
    },
    track(topic) {
      if (window._hmt) window._hmt.push(['_trackEvent', '首页题材', '查看分析', topic.unique_key])
    },
    onVisibility() {
      clearTimeout(this.timer)
      if (!document.hidden) this.fetchTopics()
    },
    applyHistoryDate(date) {
      const target = date || this.latestNavigableDate
      this.selectedDate = target && target !== this.liveTradeDate ? target : ''
      this.pickerDate = target
      clearTimeout(this.timer)
      this.error = ''
      this.data = null
      this.fetchTopics()
    },
    goOlder() {
      if (!this.canGoOlder) return
      this.applyHistoryDate(this.historyDates[this.navigationDateIndex + 1])
    },
    goNewer() {
      if (!this.canGoNewer) return
      const nextIndex = this.navigationDateIndex - 1
      this.applyHistoryDate(nextIndex >= 0 ? this.historyDates[nextIndex] : this.liveTradeDate)
    },
    onCalendarChange(date) {
      if (date) this.applyHistoryDate(date)
    },
    isHistoryDateDisabled(ymd) {
      return !this.historyDates.includes(ymd) && ymd !== this.liveTradeDate
    },
    async fetchHistoryDates() {
      try {
        const response = await request_zzquant({ url: '/v3/topic/home-topics/history-dates?limit=60', noRedirect: true })
        const body = response && response.data
        if (body && body.code === 20000 && Array.isArray(body.data)) {
          this.historyDates = body.data
          if (!this.pickerDate) this.pickerDate = this.liveTradeDate || this.historyDates[0] || ''
        }
      } catch (e) {
        this.historyDates = []
      }
    },
    async fetchTopics() {
      if (this.disposed || document.hidden) return
      clearTimeout(this.timer)
      const requestVersion = ++this.requestVersion
      const requestedDate = this.selectedDate
      this.loading = true
      try {
        const query = requestedDate ? `?date=${encodeURIComponent(requestedDate)}` : ''
        const response = await request_zzquant({ url: '/v3/topic/home-topics' + query, noRedirect: true, timeout: 60000 })
        const body = response && response.data
        if (!body || body.code !== 20000 || !body.data || !Array.isArray(body.data.items)) throw new Error('排名数据不可用')
        if (this.disposed || requestVersion !== this.requestVersion) return
        this.data = body.data
        if (!requestedDate) this.liveTradeDate = body.data.trade_date || this.liveTradeDate
        this.pickerDate = requestedDate || body.data.trade_date || this.pickerDate
        this.error = ''
      } catch (e) {
        if (!this.disposed && requestVersion === this.requestVersion) this.error = this.data ? '更新暂时失败，以下保留上次排名，请留意行情日期和计算时间。' : '暂时无法获取题材排名，请稍后重试，或进入题材库查看。'
      } finally {
        if (requestVersion === this.requestVersion) {
          this.loading = false
          if (!this.disposed && !document.hidden && !this.selectedDate) this.timer = setTimeout(this.fetchTopics, ((this.data && this.data.refresh_seconds) || 60) * 1000)
        }
      }
    }
  }
}
</script>

<style scoped>
.topic-highlights { --topic-bg:#fff; --topic-ink:#202d3c; --topic-muted:#647184; --topic-border:#e2e7ed; --topic-accent:#bf4b38; margin:10px 0 14px; color:var(--topic-ink); }
.highlights-header { min-height:34px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.title-group,.header-actions,.snapshot-label,.topic-topline,.topic-heading,.topic-rank,.topic-mainline,.topic-metrics { display:flex; align-items:center; }
.title-group { gap:9px; min-width:0; }.eyebrow { padding:3px 7px; border-radius:4px; background:#f5e9e6; color:var(--topic-accent); font-size:11px; font-weight:700; white-space:nowrap; }
h2 { font-size:20px; line-height:1.2; font-weight:700; margin:0; }.top-badge { color:var(--topic-accent); font-size:11px; font-weight:700; }
.header-actions { gap:16px; font-size:12px; white-space:nowrap; }.snapshot-label { gap:6px; color:var(--topic-muted); }.snapshot-label i { width:6px; height:6px; background:#42a66a; border-radius:50%; }
.history-navigator { height:27px; display:flex; align-items:center; overflow:hidden; border:1px solid var(--topic-border); border-radius:5px; background:var(--topic-bg); }
.history-navigator > .btn { width:26px; height:25px; padding:0; border:0; border-radius:0; color:var(--topic-muted); }
.history-navigator .b-form-btn-label-control.form-control { width:27px; min-height:25px; height:25px; padding:0; border:0; border-radius:0; background:transparent; }
.history-navigator .b-form-btn-label-control.form-control > .btn { width:26px; padding:0; color:#47749e; }
.history-date { min-width:72px; padding:0 4px; color:var(--topic-ink); font-size:11px; text-align:center; font-variant-numeric:tabular-nums; }
.library-link { font-weight:600; }.library-link span { margin-left:3px; }
.snapshot-meta { height:30px; display:flex; align-items:center; gap:10px; font-size:11px; color:var(--topic-muted); }
.snapshot-meta button { background:none; border:0; color:#47749e; padding:2px 4px; font-size:11px; cursor:pointer; }.snapshot-meta button:disabled { opacity:.5; cursor:default; }
.topic-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
.topic-grid.topic-count-1 { grid-template-columns:minmax(0,1fr); }
.topic-grid.topic-count-2 { grid-template-columns:repeat(2,minmax(0,1fr)); }
.topic-grid.topic-count-1 .topic-highlight { display:grid; grid-template-columns:minmax(320px,1fr) auto; align-items:center; gap:4px 22px; }
.topic-grid.topic-count-1 .topic-topline { height:auto; }
.topic-grid.topic-count-1 .topic-mainline { margin:0; }
.topic-grid.topic-count-1 .breadth-track { grid-column:1/-1; width:100%; margin-top:2px; }
.topic-highlight { background:var(--topic-bg); border:1px solid var(--topic-border); border-radius:9px; padding:10px 13px 7px; min-width:0; overflow:hidden; }
.topic-first { box-shadow:inset 3px 0 0 var(--topic-accent); }
.topic-topline { justify-content:space-between; gap:8px; height:22px; min-width:0; }
.topic-heading { gap:7px; min-width:0; }.topic-rank { flex:none; color:var(--topic-muted); font-size:10px; font-weight:600; }.topic-rank b { color:#738093; font-size:12px; }.topic-first .topic-rank,.topic-first .topic-rank b { color:var(--topic-accent); }
.strength-score { padding:2px 6px; border-radius:10px; background:#f3f5f7; color:var(--topic-muted); font-size:10px; font-variant-numeric:tabular-nums; }.strength-score b { color:var(--topic-ink); font-size:12px; }
.topic-mainline { justify-content:space-between; gap:12px; margin:7px 0 4px; min-width:0; }
h3 { min-width:0; margin:0; font-size:16px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }h3 a { color:inherit; }
.topic-return { flex:none; font-size:24px; line-height:1; font-variant-numeric:tabular-nums; }.topic-return.is-up { color:#c84435; }.topic-return.is-down { color:#269c50; }.topic-return.is-flat { color:var(--topic-muted); }.topic-return small { font-size:14px; }
.topic-metrics { gap:18px; color:var(--topic-muted); font-size:11px; }.topic-metrics b { margin-left:3px; color:var(--topic-ink); font-size:13px; font-variant-numeric:tabular-nums; }.topic-metrics small { margin-left:2px; font-size:10px; }
.breadth-track { height:3px; margin:7px 0 2px; background:var(--topic-border); border-radius:4px; overflow:hidden; }.breadth-track span { display:block; height:100%; background:#c16b5a; }
.ranking-method { margin-top:6px; font-size:10px; color:var(--topic-muted); line-height:1.6; }.ranking-method summary { display:inline-block; cursor:pointer; }.ranking-method p { max-width:1100px; margin:6px 0; }
.ranking-notice,.ranking-empty { padding:12px; background:var(--topic-bg); border:1px solid var(--topic-border); border-radius:8px; font-size:12px; }
@media(max-width:900px) { .topic-grid { display:flex; gap:8px; overflow-x:auto; scroll-snap-type:x mandatory; padding-bottom:3px; scrollbar-width:none; }.topic-grid::-webkit-scrollbar { display:none; }.topic-highlight { flex:0 0 calc(50% - 4px); padding:10px; scroll-snap-align:start; }.topic-grid.topic-count-1 .topic-highlight { display:block; flex-basis:100%; }.topic-grid.topic-count-1 .topic-mainline { margin:7px 0 4px; }.topic-metrics { gap:10px; }.topic-return { font-size:21px; } }
@media(max-width:650px) { .topic-highlights { margin-top:8px; }.highlights-header { align-items:flex-start; }.title-group { gap:6px; }h2 { font-size:18px; }.snapshot-label { display:none; }.header-actions { gap:8px; }.history-date { min-width:62px; }.snapshot-meta { justify-content:space-between; }.topic-highlight { flex-basis:84vw; }.ranking-method { margin-top:3px; } }
</style>

<style>
.theme-dark .topic-highlights { --topic-bg:#202734; --topic-ink:#e6ebf2; --topic-muted:#acb6c5; --topic-border:#3b4554; }.theme-dark .topic-highlights .eyebrow { background:#412e2e; }.theme-dark .topic-highlights .strength-score { background:#2b3442; }
</style>
